import type { PageBlock } from "@/utils/customPageStore";
import AdminEditField from "@/components/feature/AdminEditField";
import {
  SectionAdminBar,
  useBlockConfig,
  ArrayDeleteButton,
  AddItemButton,
} from "./shared";

interface InfoCardsBlockProps {
  block: PageBlock;
  isAdmin: boolean;
  isDark: boolean;
  headingFont: string;
  headingStyle: React.CSSProperties;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  onUpdate?: (b: PageBlock) => void;
}

export default function InfoCardsBlock({
  block,
  isAdmin,
  isDark,
  headingFont,
  headingStyle,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  onUpdate,
}: InfoCardsBlockProps) {
  const { get, setConfig } = useBlockConfig(block, onUpdate);
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-600";

  const eyebrow = get("eyebrow", "");
  const heading = get("heading", "");
  const subheading = get("subheading", "");
  const items = get("items", []) as { icon: string; title: string; body: string }[];
  const closingNote = get("closingNote", "");

  const setItem = (idx: number, key: "icon" | "title" | "body", val: string) => {
    const newItems = items.map((item, i) => (i === idx ? { ...item, [key]: val } : item));
    setConfig("items", newItems);
  };

  const deleteItem = (idx: number) => {
    setConfig("items", items.filter((_, i) => i !== idx));
  };

  const addItem = () => {
    setConfig("items", [
      ...items,
      { icon: "ri-gift-line", title: "New Feature", body: "Describe what this feature includes..." },
    ]);
  };

  return (
    <div className={`mb-8 ${isAdmin ? "rounded-xl overflow-hidden border border-green-200" : ""}`}>
      {isAdmin && (
        <SectionAdminBar
          layout="info-cards"
          isDark={isDark}
          onDelete={onDelete}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          isFirst={isFirst}
          isLast={isLast}
        />
      )}
      <div className="py-16 px-6 rounded-2xl" style={{ background: isDark ? "#1f2937" : "#fff9fb" }}>
        <div className="text-center mb-10">
          {eyebrow && (
            <AdminEditField value={eyebrow} onSave={(v) => setConfig("eyebrow", v)} inputClassName="text-center text-sm">
              <span className="text-green-500 text-sm font-bold uppercase tracking-widest">{eyebrow}</span>
            </AdminEditField>
          )}
          {heading && (
            <AdminEditField value={heading} onSave={(v) => setConfig("heading", v)} inputClassName="text-center text-3xl md:text-4xl font-extrabold">
              <h2 className={`text-3xl md:text-4xl font-extrabold mt-3 mb-3 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>
                {heading}
              </h2>
            </AdminEditField>
          )}
          {subheading && (
            <AdminEditField value={subheading} onSave={(v) => setConfig("subheading", v)} multiline inputClassName="text-center">
              <p className={`text-base max-w-2xl mx-auto ${subColor}`}>{subheading}</p>
            </AdminEditField>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((item, i) => (
            <div
              key={i}
              className={`rounded-2xl p-5 border flex gap-4 items-start relative group/item ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-white border-pink-100"
              }`}
            >
              {isAdmin && onUpdate && <ArrayDeleteButton onDelete={() => deleteItem(i)} />}
              <div
                className={`w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0 ${
                  isDark ? "bg-pink-900/40" : "bg-pink-50"
                }`}
              >
                {isAdmin && onUpdate ? (
                  <div className="flex flex-col items-center gap-1">
                    <i className={`${item.icon} text-pink-500 text-xl`}></i>
                    <AdminEditField
                      value={item.icon}
                      onSave={(v) => setItem(i, "icon", v)}
                      inputClassName="text-[10px] w-full text-center"
                    >
                      <span className="text-[10px] text-pink-400">{item.icon}</span>
                    </AdminEditField>
                  </div>
                ) : (
                  <i className={`${item.icon} text-pink-500 text-xl`}></i>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <AdminEditField
                  value={item.title}
                  onSave={(v) => setItem(i, "title", v)}
                  inputClassName="font-bold text-sm"
                >
                  <h4 className={`font-bold text-sm mb-1 ${headingColor}`}>{item.title}</h4>
                </AdminEditField>
                <AdminEditField
                  value={item.body}
                  onSave={(v) => setItem(i, "body", v)}
                  multiline
                  inputClassName="text-sm leading-relaxed"
                >
                  <p className={`text-sm leading-relaxed ${subColor}`}>{item.body}</p>
                </AdminEditField>
              </div>
            </div>
          ))}
          {isAdmin && onUpdate && (
            <AddItemButton onClick={addItem} label="Add Item" isDark={isDark} className="p-5" />
          )}
        </div>

        {closingNote && (
          <div className="text-center mt-8">
            <AdminEditField value={closingNote} onSave={(v) => setConfig("closingNote", v)} inputClassName="text-center">
              <div
                className="inline-flex items-center gap-3 rounded-full px-8 py-4"
                style={{ background: "linear-gradient(135deg, #fce7f3 0%, #dcfce7 100%)" }}
              >
                <i className="ri-checkbox-circle-fill text-green-500 text-xl"></i>
                <span className={`font-semibold text-base ${isDark ? "text-white" : "text-gray-800"}`}>
                  {closingNote}
                </span>
              </div>
            </AdminEditField>
          </div>
        )}
      </div>
    </div>
  );
}